import { FaFlask, FaCheckCircle, FaTimesCircle, FaUserInjured } from "react-icons/fa";
import { MdLocalHospital } from "react-icons/md";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";

type ExamStats = {
	interno: number;
	eletivo: number;
};

type Props = {
	totalExamsRequested: ExamStats;
	totalExamsCompleted: ExamStats;
	totalExamsCancelled: ExamStats;
};

export function ExamStatisticsCards({
	totalExamsRequested,
	totalExamsCompleted,
	totalExamsCancelled,
}: Props) {
	const totalRequested = totalExamsRequested.interno + totalExamsRequested.eletivo;
	const totalCompleted = totalExamsCompleted.interno + totalExamsCompleted.eletivo;
	const totalCancelled = totalExamsCancelled.interno + totalExamsCancelled.eletivo;

	const completionRate =
		totalRequested > 0 ? ((totalCompleted / totalRequested) * 100).toFixed(1) : "0";
	const cancellationRate =
		totalRequested > 0 ? ((totalCancelled / totalRequested) * 100).toFixed(1) : "0";

	return (
		<Row className="p-3">
			{/* Card Principal - Total de Exames Solicitados */}
			<Col sm={12} lg={4} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Body className="p-4">
						<div className="d-flex align-items-center justify-content-between mb-3">
							<div className="d-flex align-items-center">
								<div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
									<FaFlask className="text-primary" size={24} />
								</div>
								<div>
									<h6 className="mb-1 fw-bold text-dark">Exames Solicitados</h6>
									<h3 className="mb-0 fw-bold text-primary">
										{totalRequested.toLocaleString()}
									</h3>
								</div>
							</div>
							<Badge bg="primary" className="fs-6 px-3 py-2">
								Total
							</Badge>
						</div>

						<div className="row g-2">
							<div className="col-6">
								<div className="d-flex align-items-center p-2 bg-light rounded">
									<MdLocalHospital className="text-success me-2" />
									<div>
										<small className="text-muted d-block">Interno</small>
										<span className="fw-bold text-success">
											{totalExamsRequested.interno.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
							<div className="col-6">
								<div className="d-flex align-items-center p-2 bg-light rounded">
									<FaUserInjured className="text-info me-2" />
									<div>
										<small className="text-muted d-block">Eletivo</small>
										<span className="fw-bold text-info">
											{totalExamsRequested.eletivo.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
						</div>
					</Card.Body>
				</Card>
			</Col>

			{/* Card - Exames Realizados */}
			<Col sm={12} lg={4} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Body className="p-4">
						<div className="d-flex align-items-center justify-content-between mb-3">
							<div className="d-flex align-items-center">
								<div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
									<FaCheckCircle className="text-success" size={24} />
								</div>
								<div>
									<h6 className="mb-1 fw-bold text-dark">Exames Realizados</h6>
									<h3 className="mb-0 fw-bold text-success">
										{totalCompleted.toLocaleString()}
									</h3>
								</div>
							</div>
							<Badge bg="success" className="fs-6 px-3 py-2">
								{completionRate}%
							</Badge>
						</div>

						<div className="row g-2">
							<div className="col-6">
								<div className="d-flex align-items-center p-2 bg-light rounded">
									<MdLocalHospital className="text-success me-2" />
									<div>
										<small className="text-muted d-block">Interno</small>
										<span className="fw-bold text-success">
											{totalExamsCompleted.interno.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
							<div className="col-6">
								<div className="d-flex align-items-center p-2 bg-light rounded">
									<FaUserInjured className="text-info me-2" />
									<div>
										<small className="text-muted d-block">Eletivo</small>
										<span className="fw-bold text-info">
											{totalExamsCompleted.eletivo.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
						</div>

						{/* Barra de Progresso */}
						<div className="mt-3">
							<div className="d-flex justify-content-between mb-1">
								<small className="text-muted">Taxa de Conclusão</small>
								<small className="text-success fw-bold">{completionRate}%</small>
							</div>
							<div className="progress" style={{ height: "8px" }}>
								<div
									className="progress-bar bg-success"
									style={{ width: `${completionRate}%` }}
								></div>
							</div>
						</div>
					</Card.Body>
				</Card>
			</Col>

			{/* Card - Exames Cancelados */}
			<Col sm={12} lg={4} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Body className="p-4">
						<div className="d-flex align-items-center justify-content-between mb-3">
							<div className="d-flex align-items-center">
								<div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
									<FaTimesCircle className="text-danger" size={24} />
								</div>
								<div>
									<h6 className="mb-1 fw-bold text-dark">Exames Cancelados</h6>
									<h3 className="mb-0 fw-bold text-danger">
										{totalCancelled.toLocaleString()}
									</h3>
								</div>
							</div>
							<Badge bg="danger" className="fs-6 px-3 py-2">
								{cancellationRate}%
							</Badge>
						</div>

						<div className="row g-2">
							<div className="col-6">
								<div className="d-flex align-items-center p-2 bg-light rounded">
									<MdLocalHospital className="text-danger me-2" />
									<div>
										<small className="text-muted d-block">Interno</small>
										<span className="fw-bold text-danger">
											{totalExamsCancelled.interno.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
							<div className="col-6">
								<div className="d-flex align-items-center p-2 bg-light rounded">
									<FaUserInjured className="text-danger me-2" />
									<div>
										<small className="text-muted d-block">Eletivo</small>
										<span className="fw-bold text-danger">
											{totalExamsCancelled.eletivo.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
						</div>

						{/* Barra de Progresso */}
						<div className="mt-3">
							<div className="d-flex justify-content-between mb-1">
								<small className="text-muted">Taxa de Cancelamento</small>
								<small className="text-danger fw-bold">{cancellationRate}%</small>
							</div>
							<div className="progress" style={{ height: "8px" }}>
								<div
									className="progress-bar bg-danger"
									style={{ width: `${cancellationRate}%` }}
								></div>
							</div>
						</div>
					</Card.Body>
				</Card>
			</Col>
		</Row>
	);
}
